import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
	useCreateLiSnMutation,
	useLazySuggestLiSnQuery,
	usePreviewLiSnMutation,
	useUpdateLiSnMutation,
} from "@/pages/Private/redux/liSn/liSn.api";

import { clearLiSnPreview } from "@/pages/Private/redux/liSn/liSn.slice";

import { useAppDispatch } from "@/redux/hooks";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, toastFailure, useSearch } from "@/utils";
import { previewFailureMessage } from "@/pages/Private/pages/LiSn/utils/previewFailureMessage";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { InputField } from "@/components/InputField/InputField";

import {
	CreateLiSn,
	CreateLiSnSchema,
	LiSn,
	LiSnPreviewError,
} from "@/pages/Private/pages/LiSn/schema/liSn";
import { List } from "@/pages/Private/pages/List/schema/list";
import { User } from "@/pages/Public/pages/Login/schema/login";

import {
	useLazyGetPersonSuggestionQuery,
	useLazyGetCompanySuggestionQuery,
	useLazyGeSuggestionQuery,
} from "@/pages/Private/redux/liPeople/liPeople.api";

import { LinkedinPersonSuggestionItem } from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { useGetProfilesWithLiSnQuery } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";
import { AutoCompleteItem, AutoComplete } from "@/components/AutoComplete/AutoComplete";
import { LiSnType } from "@/enum/li-sn.enum";

import { LiSnAutocomplete } from "./LiSnAutocomplete";
import { LiSnPreviewModal } from "./LiSnPreviewModal";
import { LiSnUrlField } from "./LiSnUrlField";
import { initialState } from "../utils/initialState";
import { resolveSourceMode } from "../../List/pages/ListCreate/utils/sourceMode";
import { AllSuggestions } from "../utils/allSuggestionsTypes";

interface CreateLiSnFormProps {
	liSn?: LiSn | null;
	list: List;
	handleSelect?: (liSn: LiSn | null) => void;
	handleDuplicate?: (liSn: LiSn | null) => void;
}

const isProfileDisconnected = (profile: User): boolean =>
	!profile.unipile ||
	profile.unipile.isLoggedIn === false ||
	profile.unipile.salesNavigatorIsLoggedIn === false;

export const CreateLiSnForm: FunctionComponent<CreateLiSnFormProps> = ({
	liSn,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liSn.${key}`);
	const dispatch = useAppDispatch();
	const [createLiSn, { isLoading, error }] = useCreateLiSnMutation();
	const [updateLiSn, { error: updateError }] = useUpdateLiSnMutation();
	const [previewLiSn, { isLoading: isPreviewLoading, error: previewError }] =
		usePreviewLiSnMutation();
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewAccessErrorKey, setPreviewAccessErrorKey] = useState<string>();

	const { query: profileQuery, setSearchValue: setProfileSearchValue } = useSearch();

	const { data: profiles } = useGetProfilesWithLiSnQuery({
		page: 1,
		limit: 20,
		searchValue: profileQuery,
		hasLiSn: true,
	});

	const allDisconnected = !!profiles?.data?.length && profiles.data.every(isProfileDisconnected);

	const { query, searchValue, setSearchValue } = useSearch();

	const {
		searchValue: companyHeadcountSearchValue,
		setSearchValue: companyHeadcountSetSearchValue,
	} = useSearch();

	const {
		query: pastCompanyListQuery,
		searchValue: pastCompanyListSearchValue,
		setSearchValue: pastCompanyListSetSearchValue,
	} = useSearch();

	const { searchValue: companyTypeSearchValue, setSearchValue: companyTypeSetSearchValue } =
		useSearch();

	const { searchValue: functionSearchValue, setSearchValue: functionSetSearchValue } = useSearch();

	const {
		query: currentJobTitleQuery,
		searchValue: currentJobTitleSearchValue,
		setSearchValue: currentJobTitleSetSearchValue,
	} = useSearch();

	const {
		query: companyHeadquarterLocationQuery,
		searchValue: companyHeadquarterLocationSearchValue,
		setSearchValue: companyHeadquarterLocationSetSearchValue,
	} = useSearch();

	const {
		query: seniorityLevelQuery,
		searchValue: seniorityLevelSearchValue,
		setSearchValue: seniorityLevelSetSearchValue,
	} = useSearch();

	const {
		query: pastJobTitleQuery,
		searchValue: pastJobTitleSearchValue,
		setSearchValue: pastJobTitleSetSearchValue,
	} = useSearch();

	const {
		query: yearsInCurrentCompanyQuery,
		searchValue: yearsInCurrentCompanySearchValue,
		setSearchValue: yearsInCurrentCompanySetSearchValue,
	} = useSearch();

	const {
		query: yearsInCurrentPositionQuery,
		searchValue: yearsInCurrentPositionSearchValue,
		setSearchValue: yearsInCurrentPositionSetSearchValue,
	} = useSearch();

	const {
		query: geographyQuery,
		searchValue: geographySearchValue,
		setSearchValue: geographySetSearchValue,
	} = useSearch();

	const {
		query: profileLanguageQuery,
		searchValue: profileLanguageSearchValue,
		setSearchValue: profileLanguageSetSearchValue,
	} = useSearch();

	const {
		query: industryQuery,
		searchValue: industrySearchValue,
		setSearchValue: industrySetSearchValue,
	} = useSearch();

	const {
		query: yearsOfExperienceQuery,
		searchValue: yearsOfExperienceSearchValue,
		setSearchValue: yearsOfExperienceSetSearchValue,
	} = useSearch();

	const {
		query: connectionsOfQuery,
		searchValue: connectionsOfSearchValue,
		setSearchValue: connectionsOfSetSearchValue,
	} = useSearch();

	const {
		query: schoolQuery,
		searchValue: schoolSearchValue,
		setSearchValue: schoolSetSearchValue,
	} = useSearch();

	const [allSuggestions, setAllSuggestions] = useState<AllSuggestions>();

	const [fetchTags] = useLazyGeSuggestionQuery();

	const [fetchCompanies, { isFetching: companiesLoading }] = useLazyGetCompanySuggestionQuery();

	const [fetchPeople] = useLazyGetPersonSuggestionQuery();

	const [fetchLiSnSuggestions] = useLazySuggestLiSnQuery();

	useEffect(() => {
		if (profileLanguageQuery) {
			fetchLiSnSuggestions({
				type: "filter_languages",
				query: "a",
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						profileLanguage: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [profileLanguageQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (connectionsOfQuery) {
			fetchPeople({
				keyword: connectionsOfQuery,
			}).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						connectionsOf: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [connectionsOfQuery, fetchPeople]);

	useEffect(() => {
		if (schoolQuery) {
			fetchLiSnSuggestions({
				type: "filter_school_suggestions",
				query: schoolQuery,
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						school: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [schoolQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (yearsOfExperienceQuery) {
			fetchLiSnSuggestions({
				type: "filter_years_in",
				query: "a",
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						yearsOfExperience: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [yearsOfExperienceQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (geographyQuery) {
			fetchLiSnSuggestions({
				type: "filter_geography_location_region_suggestions",
				query: geographyQuery,
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						geography: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [geographyQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (industryQuery) {
			fetchLiSnSuggestions({
				type: "filter_industry_suggestions",
				query: industryQuery,
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						industry: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [industryQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (currentJobTitleQuery) {
			fetchLiSnSuggestions({
				type: "filter_job_title_suggestions",
				query: currentJobTitleQuery,
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						currentJobTitle: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [currentJobTitleQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (yearsInCurrentPositionQuery) {
			fetchLiSnSuggestions({
				type: "filter_years_in",
				query: "a",
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				})) as LinkedinPersonSuggestionItem[];

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						yearsInCurrentPosition: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [yearsInCurrentPositionQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (yearsInCurrentCompanyQuery) {
			fetchLiSnSuggestions({
				type: "filter_years_in",
				query: "a",
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						yearsInCurrentCompany: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [yearsInCurrentCompanyQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (pastJobTitleQuery) {
			fetchLiSnSuggestions({
				type: "filter_job_title_suggestions",
				query: pastJobTitleQuery,
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						pastJobTitle: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [pastJobTitleQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (seniorityLevelQuery) {
			fetchLiSnSuggestions({
				type: "filter_seniority_level",
				query: "a",
			}).then((results) => {
				const optionValues = results?.data?.data.map((item) => ({
					urn: item.id,
					title: item.displayValue,
				}));

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						seniorityLevel: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [seniorityLevelQuery, fetchLiSnSuggestions]);

	useEffect(() => {
		if (companyHeadquarterLocationQuery) {
			fetchTags({
				type: "location",
				keyword: companyHeadquarterLocationQuery,
			}).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						companyHeadquarterLocation: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [companyHeadquarterLocationQuery, fetchTags]);

	useEffect(() => {
		if (pastCompanyListQuery) {
			fetchCompanies({ keyword: pastCompanyListQuery }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						pastCompanyList: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [fetchCompanies, pastCompanyListQuery]);

	useEffect(() => {
		if (query) {
			fetchCompanies({ keyword: query }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setAllSuggestions({
						...allSuggestions,
						currentCompany: [...optionValues] as LinkedinPersonSuggestionItem[],
					});
				}
			});
		}
	}, [fetchCompanies, query]);

	const {
		handleSubmit,
		reset,
		formState: { errors },
		setValue,
		watch,
	} = useForm<CreateLiSn>({
		defaultValues: {
			...initialState,
			type: resolveSourceMode(Object.values(LiSnType), LiSnType.SPECIFIC),
		},
		resolver: zodResolver(CreateLiSnSchema),
	});

	const currentFormState = watch();

	const updateFormValue = <TName extends keyof CreateLiSn>(
		name: TName,
		value: CreateLiSn[TName]
	) => {
		setValue(name, value as never, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
	};

	const updateFormValues = async (
		data: { value: CreateLiSn[keyof CreateLiSn]; name: keyof CreateLiSn }[]
	) => {
		data.forEach(({ name, value }) => {
			setValue(name, value as never, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});
		});
	};

	useEffect(() => {
		if (liSn) {
			const formState = {
				...initialState,
				name: liSn.name,
				currentCompany: liSn.currentCompany ?? [],
				pastCompanyList: liSn.pastCompanyList ?? [],
				currentJobTitle: liSn.currentJobTitle ?? [],
				pastJobTitle: liSn.pastJobTitle ?? [],
				yearsInCurrentCompany: liSn.yearsInCurrentCompany ?? [],
				yearsInCurrentPosition: liSn.yearsInCurrentPosition ?? [],
				school: liSn.school ?? [],
				profileLanguage: liSn.profileLanguage ?? [],
				industry: liSn.industry ?? [],
				seniorityLevel: liSn.seniorityLevel ?? [],
				companyHeadcount: liSn.companyHeadcount ?? [],
				companyType: liSn.companyType ?? [],
				yearsOfExperience: liSn.yearsOfExperience ?? [],
				geography: liSn.geography ?? [],
				function: liSn.function ?? [],
				companyHeadquarterLocation: liSn.companyHeadquarterLocation ?? [],
				connectionsOf: liSn.connectionsOf ?? [],
				followingYourCompany: liSn.followingYourCompany ?? false,
				viewedYourProfileRecently: liSn.viewedYourProfileRecently ?? false,
				pastColleague: liSn.pastColleague ?? false,
				sharedExperiences: liSn.sharedExperiences ?? false,
				changedJob: liSn.changedJob ?? false,
				postedOnLinkedin: liSn.postedOnLinkedin ?? false,
				firstName: liSn.firstName ?? "",
				lastName: liSn.lastName ?? "",
				plannedScrapingUserId: liSn.plannedScrapingUserId ?? null,
				type: liSn.type ?? LiSnType.SPECIFIC,
				url: liSn.url ?? "",
			};

			if (handleDuplicate) {
				reset({
					...formState,
				});
			} else {
				reset({
					...formState,
					id: liSn.id,
				});
			}
		} else {
			reset();
		}
	}, [liSn, reset, handleDuplicate]);

	const formErrors = Object.entries(errors)
		.filter(([field]) => field !== "url")
		.map(([, error]) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateLiSn) => {
		try {
			if (liSn?.id && !handleDuplicate) {
				await updateLiSn({
					...values,
					id: liSn.id,
					listId: list.id,
				}).unwrap();
			} else {
				await createLiSn({
					...values,
					listId: list.id,
				}).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);
		} catch (err) {
			toastFailure(err, t("errors.saveFailed"));
		}
	};

	const onPreview = async (values: CreateLiSn) => {
		try {
			setPreviewAccessErrorKey(undefined);
			dispatch(clearLiSnPreview());
			setIsPreviewOpen(true);
			const preview = await previewLiSn({
				...values,
				listId: list.id,
				limit: 10,
			}).unwrap();

			if (preview.error === LiSnPreviewError.NO_USER_FOUND) {
				setPreviewAccessErrorKey(
					values.plannedScrapingUserId
						? "liSn.preview.selectedUserNoSalesNavigatorAccess"
						: "liSn.preview.organizationNoSalesNavigatorAccess"
				);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setPreviewAccessErrorKey(undefined);
		dispatch(clearLiSnPreview());
	};

	const previewErrorMessage = previewFailureMessage(
		previewAccessErrorKey && t(previewAccessErrorKey),
		previewError,
		formErrors
	);

	const buttonTitle =
		handleDuplicate && liSn?.id
			? t("basics.copyCriteria")
			: liSn?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white min-w-[380px]">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						updateFormValue("name", event.target.value);
					}}
					label={"Source name"}
					name={"name"}
					placeholder={"Enter source name"}
					value={currentFormState.name || ""}
				/>
			</div>
			{allDisconnected && (
				<div
					className="mb-4 p-3 rounded-xl border border-status-warning bg-status-warning-light text-sm text-status-warning"
					role="alert"
				>
					{t("liSn.scrapingUser.allDisconnectedWarning")}
				</div>
			)}
			<div className="flex flex-row w-full mb-4">
				<AutoComplete
					data={
						profiles?.data?.map((c) => {
							return {
								id: c.id,
								title: c.firstName + " " + c.lastName,
								rightContent: isProfileDisconnected(c) ? (
									<span className="text-xs text-status-error bg-status-error-light px-2 py-0.5 rounded-full">
										{t("liSn.scrapingUser.disconnected")}
									</span>
								) : undefined,
							} as AutoCompleteItem;
						}) || []
					}
					handleSearch={(value: string) => setProfileSearchValue(value)}
					handleSelect={async (item?: AutoCompleteItem) => {
						updateFormValue("plannedScrapingUserId", item ? Number(item.id) : null);
					}}
					information="Select a user to scrape. Leave empty for random profile. The user needs to be logged in with linkedin and have sales navigator access."
					label={"Select scraping signalist user"}
					placeholder="Leave empty for random profile"
					selectedItem={
						currentFormState.plannedScrapingUserId
							? profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
									?.firstName +
								" " +
								profiles?.data.find((p) => p.id === currentFormState.plannedScrapingUserId)
									?.lastName
							: ""
					}
				/>
			</div>
			{currentFormState.type === LiSnType.SEARCH && (
				<>
					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("currentCompany")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"currentCompany" as keyof CreateLiSn}
							loading={companiesLoading}
							organizationSuggestions={allSuggestions?.currentCompany}
							searchValue={searchValue}
							setSearchValue={setSearchValue}
							value={currentFormState.currentCompany}
						/>
					</div>
					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("companyHeadcount")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"companyHeadcount" as keyof CreateLiSn}
							organizationSuggestions={[
								{
									urn: "A",
									title: "Self-employed",
								},
								{
									urn: "B",
									title: "1-10",
								},
								{
									urn: "C",
									title: "11-50",
								},
								{
									urn: "D",
									title: "51-200",
								},
								{
									urn: "E",
									title: "201-500",
								},
								{
									urn: "F",
									title: "501-1000",
								},
								{
									urn: "G",
									title: "1001-5000",
								},
								{
									urn: "H",
									title: "5001-10000",
								},
								{
									urn: "I",
									title: "10000+",
								},
							].filter((item) =>
								item.title.toLowerCase().includes(companyHeadcountSearchValue.toLowerCase())
							)}
							searchValue={companyHeadcountSearchValue}
							setSearchValue={companyHeadcountSetSearchValue}
							value={currentFormState.companyHeadcount}
						/>
					</div>
					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("pastCompanyList")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"pastCompanyList" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.pastCompanyList}
							searchValue={pastCompanyListSearchValue}
							setSearchValue={pastCompanyListSetSearchValue}
							value={currentFormState.pastCompanyList}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("companyType")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"companyType" as keyof CreateLiSn}
							organizationSuggestions={[
								{
									urn: "C",
									title: "Public Company",
								},
								{
									urn: "P",
									title: "Privately Held",
								},
								{
									urn: "D",
									title: "Educational Institution",
								},
								{
									urn: "N",
									title: "Non Profit",
								},
								{
									urn: "S",
									title: "Partnership",
								},
								{
									urn: "E",
									title: "Self Employed",
								},
								{
									urn: "O",
									title: "Self Owned",
								},
								{
									urn: "G",
									title: "Government Agency",
								},
							].filter((item) =>
								item.title.toLowerCase().includes(companyTypeSearchValue.toLowerCase())
							)}
							searchValue={companyTypeSearchValue}
							setSearchValue={companyTypeSetSearchValue}
							value={currentFormState.companyType}
						/>
					</div>
					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("headquarterLocation")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"companyHeadquarterLocation" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.companyHeadquarterLocation}
							searchValue={companyHeadquarterLocationSearchValue}
							setSearchValue={companyHeadquarterLocationSetSearchValue}
							value={currentFormState.companyHeadquarterLocation}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("function")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"function" as keyof CreateLiSn}
							organizationSuggestions={[
								{
									urn: "1",
									title: "Accounting",
								},
								{
									urn: "2",
									title: "Administrative",
								},
								{
									urn: "3",
									title: "Arts and Design",
								},
								{
									urn: "4",
									title: "Business Development",
								},
								{
									urn: "5",
									title: "Community and Social Services",
								},
								{
									urn: "6",
									title: "Consulting",
								},
								{
									urn: "7",
									title: "Education",
								},
								{
									urn: "8",
									title: "Engineering",
								},
								{
									urn: "9",
									title: "Entrepreneurship",
								},
								{
									urn: "10",
									title: "Finance",
								},
								{
									urn: "11",
									title: "Healthcare Services",
								},
								{
									urn: "12",
									title: "Human Resources",
								},
								{
									urn: "13",
									title: "Information Technology",
								},
								{
									urn: "14",
									title: "Legal",
								},
								{
									urn: "15",
									title: "Marketing",
								},
								{
									urn: "16",
									title: "Media and Communication",
								},
								{
									urn: "17",
									title: "Military and Protective Services",
								},
								{
									urn: "18",
									title: "Operations",
								},
								{
									urn: "19",
									title: "Product Management",
								},
								{
									urn: "20",
									title: "Program and Project Management",
								},
								{
									urn: "21",
									title: "Purchasing",
								},
								{
									urn: "22",
									title: "Quality Assurance",
								},
								{
									urn: "23",
									title: "Real Estate",
								},
								{
									urn: "24",
									title: "Research",
								},
								{
									urn: "25",
									title: "Sales",
								},
								{
									urn: "26",
									title: "Support",
								},
							].filter((item) =>
								item?.title?.toLowerCase().includes(functionSearchValue.toLowerCase())
							)}
							searchValue={functionSearchValue}
							setSearchValue={functionSetSearchValue}
							value={currentFormState.function}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("currentJobTitle")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"currentJobTitle" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.currentJobTitle}
							ownValue={false}
							searchValue={currentJobTitleSearchValue}
							setSearchValue={currentJobTitleSetSearchValue}
							value={currentFormState.currentJobTitle}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("seniorityLevel")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"seniorityLevel" as keyof CreateLiSn}
							organizationSuggestions={[
								{ title: "Owner / Partner", urn: "320" },
								{ title: "CXO", urn: "310" },
								{ title: "Vice President", urn: "300" },
								{ title: "Director", urn: "220" },
								{ title: "Experienced Manager", urn: "210" },
								{ title: "Entry Level Manager", urn: "200" },
								{ title: "Strategic", urn: "130" },
								{ title: "Senior", urn: "120" },
								{ title: "Entry Level", urn: "110" },
								{ title: "In Training", urn: "100" },
							].filter((item) =>
								seniorityLevelSearchValue
									? item.title.toLowerCase().includes(seniorityLevelSearchValue.toLowerCase())
									: true
							)}
							searchValue={seniorityLevelSearchValue}
							setSearchValue={seniorityLevelSetSearchValue}
							value={currentFormState.seniorityLevel}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("pastJobTitle")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"pastJobTitle" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.pastJobTitle}
							ownValue={true}
							searchValue={pastJobTitleSearchValue}
							setSearchValue={pastJobTitleSetSearchValue}
							value={currentFormState.pastJobTitle}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("yearsInCurrentCompany")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"yearsInCurrentCompany" as keyof CreateLiSn}
							organizationSuggestions={[
								{ title: "Less than 1 year", urn: "1" },
								{ title: "1 to 2 years", urn: "2" },
								{ title: "3 to 5 years", urn: "3" },
								{ title: "6 to 10 years", urn: "4" },
								{ title: "More than 10 years", urn: "5" },
							].filter((item) =>
								yearsInCurrentCompanySearchValue
									? item.title
											.toLowerCase()
											.includes(yearsInCurrentCompanySearchValue.toLowerCase())
									: true
							)}
							searchValue={yearsInCurrentCompanySearchValue}
							setSearchValue={yearsInCurrentCompanySetSearchValue}
							value={currentFormState.yearsInCurrentCompany}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("yearsInCurrentPosition")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"yearsInCurrentPosition" as keyof CreateLiSn}
							organizationSuggestions={[
								{ title: "Less than 1 year", urn: "1" },
								{ title: "1 to 2 years", urn: "2" },
								{ title: "3 to 5 years", urn: "3" },
								{ title: "6 to 10 years", urn: "4" },
								{ title: "More than 10 years", urn: "5" },
							].filter((item) =>
								yearsInCurrentPositionSearchValue
									? item.title
											.toLowerCase()
											.includes(yearsInCurrentPositionSearchValue.toLowerCase())
									: true
							)}
							searchValue={yearsInCurrentPositionSearchValue}
							setSearchValue={yearsInCurrentPositionSetSearchValue}
							value={currentFormState.yearsInCurrentPosition}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">Person location</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"geography" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.geography}
							searchValue={geographySearchValue}
							setSearchValue={geographySetSearchValue}
							value={currentFormState.geography}
						/>
					</div>
					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("industry")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"industry" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.industry}
							searchValue={industrySearchValue}
							setSearchValue={industrySetSearchValue}
							value={currentFormState.industry}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<InputField
							error={!!errors.firstName?.message}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								updateFormValue("firstName", event.target.value);
							}}
							label={ts("firstName")}
							name={"firstName"}
							placeholder={ts("firstnamePlaceholder")}
							value={currentFormState.firstName || ""}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<InputField
							error={!!errors.lastName?.message}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								updateFormValue("lastName", event.target.value);
							}}
							label={ts("lastName")}
							name={"lastName"}
							placeholder={ts("lastnamePlaceholder")}
							value={currentFormState.lastName || ""}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("profileLanguage")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"profileLanguage" as keyof CreateLiSn}
							organizationSuggestions={[
								{ title: "Arabic", urn: "ar" },
								{ title: "English", urn: "en" },
								{ title: "Spanish", urn: "es" },
								{ title: "Portuguese", urn: "pt" },
								{ title: "Chinese", urn: "zh" },
								{ title: "French", urn: "fr" },
								{ title: "Italian", urn: "it" },
								{ title: "Russian", urn: "ru" },
								{ title: "German", urn: "de" },
								{ title: "Dutch", urn: "nl" },
								{ title: "Turkish", urn: "tr" },
								{ title: "Tagalog", urn: "tl" },
								{ title: "Polish", urn: "pl" },
								{ title: "Korean", urn: "ko" },
								{ title: "Japanese", urn: "ja" },
								{ title: "Malay", urn: "ms" },
								{ title: "Norwegian", urn: "no" },
								{ title: "Danish", urn: "da" },
								{ title: "Romanian", urn: "ro" },
								{ title: "Swedish", urn: "sv" },
								{ title: "Bahasa Indonesia", urn: "in" },
								{ title: "Czech", urn: "cs" },
							].filter((item) =>
								profileLanguageSearchValue
									? item.title.toLowerCase().includes(profileLanguageSearchValue.toLowerCase())
									: true
							)}
							searchValue={profileLanguageSearchValue}
							setSearchValue={profileLanguageSetSearchValue}
							value={currentFormState.profileLanguage}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("yearsOfExperience")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"yearsOfExperience" as keyof CreateLiSn}
							organizationSuggestions={[
								{ title: "Less than 1 year", urn: "1" },
								{ title: "1 to 2 years", urn: "2" },
								{ title: "3 to 5 years", urn: "3" },
								{ title: "6 to 10 years", urn: "4" },
								{ title: "More than 10 years", urn: "5" },
							].filter((item) =>
								yearsOfExperienceSearchValue
									? item.title.toLowerCase().includes(yearsOfExperienceSearchValue.toLowerCase())
									: true
							)}
							searchValue={yearsOfExperienceSearchValue}
							setSearchValue={yearsOfExperienceSetSearchValue}
							value={currentFormState.yearsOfExperience}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("school")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"school" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.school}
							searchValue={schoolSearchValue}
							setSearchValue={schoolSetSearchValue}
							value={currentFormState.school}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<label className="text-sm mb-2">{ts("connectionsOf")}</label>
						<LiSnAutocomplete
							handleChange={updateFormValues}
							item={"connectionsOf" as keyof CreateLiSn}
							organizationSuggestions={allSuggestions?.connectionsOf?.map((item) => ({
								urn: item.urn,
								title: `${item.title} (${item.subtitle})`,
							}))}
							searchValue={connectionsOfSearchValue}
							setSearchValue={connectionsOfSetSearchValue}
							value={currentFormState.connectionsOf}
						/>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.followingYourCompany || false}
							name={"followingYourCompany"}
							onChange={() => {
								updateFormValue("followingYourCompany", !currentFormState.followingYourCompany);
							}}
						>
							{ts("followingYourCompany")}
						</Checkbox>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.viewedYourProfileRecently || false}
							name={"viewedYourProfileRecently"}
							onChange={() => {
								updateFormValue(
									"viewedYourProfileRecently",
									!currentFormState.viewedYourProfileRecently
								);
							}}
						>
							{ts("viewedYourProfileRecently")}
						</Checkbox>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.pastColleague || false}
							name={"pastColleague"}
							onChange={() => {
								updateFormValue("pastColleague", !currentFormState.pastColleague);
							}}
						>
							{ts("pastColleague")}
						</Checkbox>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.sharedExperiences || false}
							name={"sharedExperiences"}
							onChange={() => {
								updateFormValue("sharedExperiences", !currentFormState.sharedExperiences);
							}}
						>
							{ts("sharedExperiences")}
						</Checkbox>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.changedJob || false}
							name={"changedJob"}
							onChange={() => {
								updateFormValue("changedJob", !currentFormState.changedJob);
							}}
						>
							{ts("changedJob")}
						</Checkbox>
					</div>

					<div className="flex flex-col w-full mb-4">
						<Checkbox
							isChecked={currentFormState.postedOnLinkedin || false}
							name={"postedOnLinkedin"}
							onChange={() => {
								updateFormValue("postedOnLinkedin", !currentFormState.postedOnLinkedin);
							}}
						>
							{ts("postedOnLinkedin")}
						</Checkbox>
					</div>
				</>
			)}

			{currentFormState.type === LiSnType.SPECIFIC && (
				<LiSnUrlField
					errorMessage={errors.url?.message}
					handleChange={(event) => updateFormValue("url", event.target.value)}
					label={ts("url")}
					name={"url"}
					placeholder={ts("urlPlaceholder")}
					value={currentFormState.url || ""}
				/>
			)}

			{getAllErrors(error || updateError, formErrors).length
				? renderErrorMessages(getAllErrors(error || updateError, formErrors))
				: null}

			<div className="flex gap-4 w-full justify-end">
				{handleSelect && (
					<div className="flex max-w-[100px] w-full">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								handleSelect(null);
								handleDuplicate?.(null);
								reset(initialState);
							}}
						/>
					</div>
				)}

				<div className="flex max-w-[150px] w-full">
					<Button
						className="w-full"
						color={ButtonColor.ACTION_SECONDARY}
						isLoading={isPreviewLoading}
						title={t("liSn.preview.button")}
						onClick={handleSubmit(onPreview)}
					/>
				</div>

				<div className="flex max-w-[150px] w-full">
					<Button
						className="w-full"
						isLoading={isLoading}
						title={buttonTitle}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>

			<LiSnPreviewModal
				errorMessage={previewErrorMessage}
				handleClose={handleClosePreview}
				isLoading={isPreviewLoading}
				isOpened={isPreviewOpen}
			/>
		</div>
	);
};
